/**
 * night_live — the LIVING night-sky TSL layer (2026-08-27 night-sky wave;
 * 2026-09-11: the photo split into a starless background + point stars):
 *
 *   BACKGROUND the starless astrophoto (Milky Way glow, dust lanes,
 *              nebulosity) — a 4K equirect; smooth content, so the pole
 *              singularity is invisible in it.
 *   STARS      every catalogued star drawn as a ROUND POINT on the sphere
 *              (lib/sky/star_field.ts): resolution-independent, no pole
 *              squeeze, per-star scintillation scaled by airmass.
 *   EXTINCTION airmass dimming + reddening toward the horizon (blue
 *              extinguished first), applied to background + stars + traffic.
 *   AIRGLOW    a faint green-tinged band brightening toward the horizon,
 *              deep-night gated (never extincted — it IS the atmosphere).
 *   TRAFFIC    the station transit + two faint ambient satellites + meteors —
 *              dots/streaks drawn where the CPU scheduler says
 *              (lib/night_sky_schedule.ts drives the uniforms; every client
 *              sees the same sky).
 *   BATTLE     the deep-space fleet engagement, visible from the ground
 *              during the shared war window: a tiny elongated cruiser with
 *              engine glow + blinking nav lights, an escort train trailing
 *              it, and pinpoint weapon flashes. Deliberately SMALL — you are
 *              on the planet; they are in space.
 *
 * Gone with the split: the equirect-cell procedural star layers (they
 * squashed toward the pole exactly like the photo — a "pole fade" hid it),
 * the micro-cell photo scintillation (each star twinkles itself now) and the
 * fractal "detail map" that faked resolution over the 8K texels.
 *
 * NO-CYCLE RULE (volcanic_planet precedent): SkyModule imports this file, so
 * the shared uniforms arrive as PARAMETERS — this file must not import from
 * SkyModule. All scheduling lives CPU-side in night_sky_schedule.ts; the
 * shader only draws what the uniforms describe.
 */
import {
  float, vec3, floor, fract, sin, pow, max, min, clamp, mix, Fn,
  smoothstep, dot, normalize, length, exp, fwidth, hash, Loop, time, cross,
} from 'three/tsl';

/* TSL node graphs are structurally typed; the augmented-operator node type is
 * impractical to name (the volcanic_planet.ts precedent). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export interface NightLiveUniforms {
  /** SkyModule's uSunDir uniform node (drives the in-shader deep-night gate). */
  sunDir: N;
  /** Live-layer master gain uniform (traffic, battle, airglow; 0 = the still sky). */
  gain: N;
  /** Background (starless photo) gain uniform. */
  bgGain: N;
  /** Mover slots: [station, sat1, sat2] — dir (unit vec3) + bright (float). */
  movers: Array<{ dir: N; bright: N }>;
  /** Meteor streak head/velocity/arc-length/brightness. */
  meteor: { dir: N; vel: N; len: N; bright: N };
  /** The fleet battle: sky dir, heading tangent, window envelope, seconds. */
  battle: { dir: N; tangent: N; active: N };
}

/** Scintillation weight along a direction: an airmass effect — strong low, calm at zenith. */
export function airmassWeight(viewDir: N): N {
  const up = clamp((viewDir as { y: N }).y, 0, 1);
  return float(0.3).add(float(0.7).mul(float(1).sub(up)));
}

/**
 * Compose the COMPLETE living night layer: background + point stars + traffic
 * + battle, extincted, plus airglow. Returns the rgb to multiply by
 * uNightFactor and add to the atmosphere.
 */
export function buildNightLive(viewDir: N, backgroundRgb: N, starsRgb: N, u: NightLiveUniforms): N {
  // r185/WebGPU LAW: toVar()/addAssign need an enclosing Fn() stack. Without
  // it TSL logs "No stack defined for assign" per assign and the WGSL
  // pipeline descriptor comes out incomplete — a per-frame
  // "createRenderPipeline: Required member is undefined" fatal on real
  // WebGPU. WebGL happened to tolerate the stackless graph, which is why
  // every headless (SwiftShader/WebGL) harness stayed green.
  return Fn(() => {
    const dirY = (viewDir as { y: N }).y;

    // ── Deep night in-shader (mirrors lighting.ts deepNight()) ────────────────
    const sunY = (u.sunDir as { y: N }).y;
    const dn0 = clamp(float(-0.10).sub(sunY).div(0.18), 0, 1);
    const deepNight = dn0.mul(dn0).mul(float(3).sub(dn0.mul(2)));

    const up = clamp(dirY, 0, 1);

    // ── EXTINCTION: airmass ≈ 1/sin(alt); blue dies first near the horizon ────
    const airmass = min(float(1).div(max(dirY, 0.035)), 14);
    const ext = pow(vec3(0.935, 0.905, 0.865), airmass.sub(1));

    // ── AIRGLOW: the faint chemiluminescent band hugging the horizon ──────────
    const airglow = vec3(0.32, 0.85, 0.55)
      .mul(0.02)
      .mul(pow(float(1).sub(up), 5))
      .mul(smoothstep(-0.06, 0.06, dirY)) // fades away under the horizon line
      .mul(deepNight);

    // ── STARS: the point field, clean seam under the horizon line ─────────────
    const stars = starsRgb.mul(smoothstep(-0.02, 0.06, dirY));

    // ── TRAFFIC: movers (station + sats) as fwidth-AA'd dots ──────────────────
    // One shared angular pixel footprint (chord units) for every point sprite.
    const dAA = clamp(length(fwidth(viewDir)), 0.00035, 0.02);
    const drawDot = (dir: N, bright: N, tint: N, glare: number): N => {
      const d = length(viewDir.sub(dir));
      const core = smoothstep(dAA.mul(3.4), dAA.mul(1.1), d);
      const halo = exp(d.mul(-170)).mul(glare);
      return tint.mul(bright).mul(core.add(halo));
    };
    const traffic = vec3(0).toVar();
    { // The station itself is a real GLB mesh now (AtlasNightSkyBodiesModule);
      // the shader keeps only a soft sun-glint halo around it.
      const dSt = length(viewDir.sub(u.movers[0].dir));
      traffic.addAssign(vec3(1, 0.97, 0.9).mul(u.movers[0].bright).mul(exp(dSt.mul(-190)).mul(0.5)));
    }
    traffic.addAssign(drawDot(u.movers[1].dir, u.movers[1].bright, vec3(0.85, 0.9, 1), 0));
    traffic.addAssign(drawDot(u.movers[2].dir, u.movers[2].bright, vec3(0.9, 0.9, 0.95), 0));

    // ── METEOR: a streak = distance to the arc segment, fading down the tail ──
    {
      const tail = normalize(u.meteor.vel).mul(-1);
      const rel = viewDir.sub(u.meteor.dir);
      const s = clamp(dot(rel, tail).div(max(u.meteor.len, 0.001)), 0, 1);
      const closest = u.meteor.dir.add(tail.mul(s.mul(u.meteor.len)));
      const d = length(viewDir.sub(closest));
      const width = dAA.mul(float(1.6).add(u.meteor.bright.mul(0.8)));
      const streak = smoothstep(width, width.mul(0.25), d)
        .mul(pow(float(1).sub(s), 2)) // hot head → fading train
        .mul(u.meteor.bright);
      traffic.addAssign(vec3(1, 0.98, 0.92).mul(streak).mul(1.6));
    }

    // ── THE FLEET BATTLE from the ground (deep-night showpiece) ───────────────
    const battle = vec3(0).toVar();
    {
      const bDir = u.battle.dir;
      const bTan = u.battle.tangent;
      const hullHalf = float(0.011); // ~1.3° hull — tiny but unmistakable
      const rel = viewDir.sub(bDir);
      const along = clamp(dot(rel, bTan), hullHalf.mul(-1), hullHalf);
      const axis = bDir.add(bTan.mul(along));
      const hull = smoothstep(dAA.mul(2.2), dAA.mul(0.7), length(viewDir.sub(axis)));
      battle.addAssign(vec3(0.95, 0.82, 0.66).mul(hull).mul(0.9)); // the sunlit hull line
      // Engine glow at the stern, gently pulsing.
      const stern = bDir.sub(bTan.mul(hullHalf));
      const eng = exp(length(viewDir.sub(stern)).mul(-600));
      battle.addAssign(vec3(0.55, 0.75, 1).mul(eng).mul(float(0.8).add(sin(time.mul(6.3)).mul(0.25))));
      // Nav lights: bow red + stern white, slow alternating blinks.
      const bow = bDir.add(bTan.mul(hullHalf));
      const blinkA = smoothstep(0.86, 0.9, fract(time.mul(0.55)));
      const blinkB = smoothstep(0.86, 0.9, fract(time.mul(0.55).add(0.5)));
      battle.addAssign(vec3(1, 0.25, 0.2).mul(exp(length(viewDir.sub(bow)).mul(-1500))).mul(blinkA).mul(2));
      battle.addAssign(vec3(1, 1, 1).mul(exp(length(viewDir.sub(stern)).mul(-1500))).mul(blinkB).mul(1.2));
      // The escort train: four dots trailing the cruiser (the satellite-train look).
      Loop(4, ({ i }: { i: N }) => {
        const k = float(i).add(1);
        const p = bDir.sub(bTan.mul(k.mul(0.011).add(0.012)));
        const dd = length(viewDir.sub(p));
        battle.addAssign(vec3(0.85, 0.9, 1).mul(smoothstep(dAA.mul(2.0), dAA.mul(0.7), dd)).mul(0.5));
      });
      // Weapon flashes: hash-timed pinpoints jittered around the engagement.
      const bit = cross(bDir, bTan);
      const fb = floor(time.mul(2.2));
      const fh = hash(fb.mul(0.731).add(3.7));
      const fOn = smoothstep(0.5, 0.55, fh); // ~half the half-second buckets fire
      const jx = hash(fb.mul(1.37).add(9.1)).sub(0.5);
      const jy = hash(fb.mul(2.11).add(4.3)).sub(0.5);
      const fPos = bDir.add(bTan.mul(jx.mul(0.05))).add(bit.mul(jy.mul(0.03)));
      const fPulse = pow(float(1).sub(fract(time.mul(2.2))), 2); // each flash decays fast
      const fCol = mix(vec3(1, 0.4, 0.25), vec3(0.5, 0.9, 1), hash(fb.add(0.5)));
      battle.addAssign(fCol.mul(exp(length(viewDir.sub(fPos)).mul(-1000))).mul(fOn).mul(fPulse).mul(3.4));
      battle.mulAssign(u.battle.active);
      battle.mulAssign(deepNight);
    }

    const preExt = traffic.add(battle).mul(u.gain);
    return backgroundRgb.mul(u.bgGain).add(stars).add(preExt).mul(ext).add(airglow.mul(u.gain));
  })();
}
